/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { When } from "react-if";
import { Input, Label, Checkbox, Text, theme, Select } from "@nordcloud/gnui";
import { ActionParameterType, KeyValue } from "~/generated/graphql";
import { NoData, BaselineDefinedSettings } from "~/components";
import { isString } from "~/components/Details/utils";
import { KeyValueList } from "~/components/KeyValueList";
import { isEmpty, isNil, isNonEmpty, isNotNil } from "~/tools";
import {
  getBaselineSourceIndex,
  getFlatPlanEntities,
  isBaselineDependentEntity,
  updateBaselineDependentEntities,
} from "~/utils";
import { isValidJSONObject } from "~/utils/json";
import {
  getPatchPolicy,
  getVisiblePatchPolicyParams,
  PatchPolicy,
  patchPolicyRules,
} from "~/utils/patchPolicyRules";
import {
  defaultActionSettingsValues,
  PlanField,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { FormData } from "~/views/plans/PlanCreate/components/PlanCreateWizard/formConfig";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import {
  FormField,
  Option,
  SelectedPlanEntity,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";
import { separateConnectedWords } from "~/views/plans/utils";
import { paramsExcludedFromBaselinePatching } from "../constant";
import { updateEntities, buildOptions } from "../utils";
import { SelectActionVersion } from "./SelectActionVersion";
import { SelectBaselinePatching } from "./SelectBaselinePatching";

type Props = {
  nextStep?: () => void;
  setTab: (key: number) => void;
};

export function AdvancedSettingsForm({ nextStep, setTab }: Props) {
  const { stepState, updateStepState } = useStepState();

  const { setPlanData } = usePlanWizard();

  const formMethods = useForm<FormData>({
    defaultValues: defaultActionSettingsValues,
  });

  const { control, handleSubmit } = formMethods;

  const findValueByKey = useCallback(
    (key: string, opt?: Option[]) => {
      const parameter = (stepState.selectedEntity?.inputParameters ?? []).find(
        (item) => item.key === key
      );

      if (Array.isArray(opt)) {
        return opt.find((o: Option) => o.value === parameter?.value)?.value;
      } else {
        return parameter?.value;
      }
    },
    [stepState.selectedEntity?.inputParameters]
  );

  const findValueByKeyOp = useCallback(
    (key: string, opt?: Option[]) => {
      const parameter = (stepState.selectedEntity?.inputParameters ?? []).find(
        (item) => item.key === key
      );

      const parameterValue =
        parameter?.value && isValidJSONObject(parameter.value)
          ? JSON.parse(parameter.value)
          : parameter?.value;
      if (isNil(parameterValue)) {
        return;
      }

      const isArrayParameter = Array.isArray(parameterValue);

      return (
        opt?.filter((o: Option) =>
          isArrayParameter
            ? parameterValue.includes(o.value)
            : parameterValue === o.value
        ) ?? undefined
      );
    },
    [stepState.selectedEntity?.inputParameters]
  );

  const handleSetPlanData = (newActions: SelectedPlanEntity[]) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: [...(newActions ?? [])],
      },
    }));
  };

  const getParamsWithNotNilValues = (params: KeyValue[]) => {
    return params.filter((param) => isNotNil(param.value));
  };

  const replaceInputParameterValue = (
    key: string,
    inputValue?: string | string[]
  ) => {
    const parameterIndex = (
      stepState.selectedEntity?.inputParameters ?? []
    ).findIndex((item) => item.key === key);

    const value = isString(inputValue)
      ? inputValue
      : JSON.stringify(inputValue);

    return parameterIndex !== -1
      ? [
          ...(stepState.selectedEntity?.inputParameters ?? []).slice(
            0,
            parameterIndex
          ),
          { key, value },
          ...(stepState.selectedEntity?.inputParameters ?? []).slice(
            parameterIndex + 1
          ),
        ]
      : [
          ...(stepState.selectedEntity?.inputParameters?.filter(
            (item) => item.key !== key
          ) ?? []),
          { key, value },
        ];
  };

  function addOrUpdateInputParameterKeyValue(
    key: string,
    inputValue?: string | string[]
  ): void {
    const inputParams = replaceInputParameterValue(key, inputValue);
    const inputParamsWithNonEmptyValues =
      getParamsWithNotNilValues(inputParams);

    const isUpdatingPatchPolicy =
      key === "PatchPolicy" &&
      typeof inputValue === "string" &&
      Object.values(PatchPolicy).includes(inputValue as PatchPolicy);

    const updatedInputParams = isUpdatingPatchPolicy
      ? inputParamsWithNonEmptyValues.map((param) => {
          if (param.key in patchPolicyRules[inputValue].defaultSettings) {
            return {
              key: param.key,
              value: patchPolicyRules[inputValue].defaultSettings[param.key],
            };
          }
          return param;
        })
      : inputParamsWithNonEmptyValues;

    const updatedEntities = updateEntities(stepState, {
      inputParameters: updatedInputParams,
    });

    const flatEntities = getFlatPlanEntities(updatedEntities);
    const baselineEntityIndex = getBaselineSourceIndex(flatEntities);

    const updatedWithDependentEntities =
      baselineEntityIndex !== -1
        ? updateBaselineDependentEntities(
            updatedEntities,
            flatEntities[baselineEntityIndex]
          )
        : updatedEntities;

    updateStepState({
      entities: updatedWithDependentEntities,
      selectedEntity: {
        ...stepState.selectedEntity,
        inputParameters: inputParamsWithNonEmptyValues,
      },
    });

    handleSetPlanData(updatedWithDependentEntities);
  }

  const handleChange = (value: string, name: string) =>
    addOrUpdateInputParameterKeyValue(name, value);

  const isEntityBaselineDependent = useMemo(
    () =>
      isBaselineDependentEntity(
        stepState.selectedEntity,
        getFlatPlanEntities(stepState.entities ?? [])
      ),
    [stepState.selectedEntity, stepState.entities]
  );

  const patchPolicy = useMemo(
    () => getPatchPolicy(stepState.selectedEntity?.inputParameters),
    [stepState.selectedEntity?.inputParameters]
  );
  const visiblePatchPolicyParams = useMemo(
    () =>
      getVisiblePatchPolicyParams(
        stepState.selectedEntity?.inputParameters,
        patchPolicy
      ),
    [stepState.selectedEntity?.inputParameters, patchPolicy]
  );

  if (isEmpty(stepState.selectedEntity?.parameterDefinitions ?? [])) {
    return (
      <>
        <SelectBaselinePatching />
        <SelectActionVersion />
        <NoData hasIcon message="There are no Advanced Action Settings" />
      </>
    );
  }

  const isDefaultCheck = (value: string, defaultValue?: string | null) => {
    if (findValueByKey(value)) {
      return findValueByKey(value) === "true";
    }
    return JSON.parse(defaultValue ?? "false");
  };

  const submit = () => {
    if (stepState.selectedEntity?.notificationGroups) {
      setTab(2);
    } else {
      nextStep?.();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form id="planSettingsForm" onSubmit={handleSubmit(submit)}>
        <SelectBaselinePatching />
        <SelectActionVersion />
        {/* eslint-disable-next-line complexity */}
        {stepState.selectedEntity?.parameterDefinitions?.map((item) => {
          if (item.name === "BaselineSource") {
            return null;
          }

          const options = buildOptions(item.allowedValues ?? []);
          const defaultValue = options?.find(
            (o: Option) => o.value === item.defaultValue
          );
          const allowedValue = options?.find(
            (o: Option) => o.value === item.allowedValues?.[0]
          );

          const val =
            findValueByKeyOp(item.name, options) ??
            defaultValue ??
            allowedValue;

          const isDefinedByBaseline =
            isEntityBaselineDependent &&
            !paramsExcludedFromBaselinePatching.includes(item.name);

          if (isDefinedByBaseline) {
            return null;
          }

          const isDefinedByPatchPolicy =
            patchPolicy &&
            patchPolicyRules[patchPolicy]?.defaultSettings &&
            item.name in patchPolicyRules[patchPolicy].defaultSettings;

          if (isDefinedByPatchPolicy) {
            return null;
          }

          const isHiddenByPatchPolicy =
            patchPolicy &&
            patchPolicyRules[patchPolicy]?.visibleParams &&
            !patchPolicyRules[patchPolicy].visibleParams?.includes(item.name);

          if (isHiddenByPatchPolicy) {
            return null;
          }

          return (
            <div
              key={item.name}
              css={{ marginBottom: theme.spacing.spacing04 }}
            >
              <Label
                name={separateConnectedWords(item.name)}
                css={{ marginBottom: 0 }}
                required={item.required}
              />
              <Text
                mb={theme.spacing.spacing02}
                size="sm"
                color={theme.color.text.text02}
                tag="div"
              >
                {item.description}
              </Text>
              <When
                condition={
                  isNonEmpty(item.allowedValues ?? []) &&
                  item.type === ActionParameterType.List
                }
              >
                <Controller
                  control={control}
                  name={FormField.PLAN_ENTITIES}
                  render={() => {
                    return (
                      <Select
                        isMulti
                        name={`Select ${separateConnectedWords(item.name)}`}
                        options={options}
                        onChange={(o) => {
                          const value = o?.map((i) => i.value);
                          addOrUpdateInputParameterKeyValue(item.name, value);
                        }}
                        value={val}
                        isSearchable={false}
                      />
                    );
                  }}
                />
              </When>
              <When
                condition={
                  isNonEmpty(item.allowedValues ?? []) &&
                  item.type === ActionParameterType.String
                }
              >
                <Controller
                  control={control}
                  name={FormField.PLAN_ENTITIES}
                  render={() => {
                    return (
                      <Select
                        name={`Select ${separateConnectedWords(item.name)}`}
                        options={options}
                        onChange={(o: Option | null) => {
                          addOrUpdateInputParameterKeyValue(
                            item.name,
                            o?.value
                          );
                        }}
                        value={val}
                        isSearchable={false}
                      />
                    );
                  }}
                />
              </When>
              <When
                condition={
                  isEmpty(item.allowedValues ?? []) &&
                  item.type !== ActionParameterType.Bool
                }
              >
                <Controller
                  control={control}
                  name={FormField.PLAN_ENTITIES}
                  render={() => {
                    return (
                      <Input
                        value={
                          findValueByKey(item.name) ?? item.defaultValue ?? ""
                        }
                        name={separateConnectedWords(item.name)}
                        id={item.name}
                        onChange={(e) =>
                          handleChange(e.target.value, item.name)
                        }
                      />
                    );
                  }}
                />
              </When>
              <When condition={item.type === ActionParameterType.Bool}>
                <Controller
                  control={control}
                  name={FormField.PLAN_ENTITIES}
                  render={() => {
                    return (
                      <Checkbox
                        id={item.name}
                        labelText={separateConnectedWords(item.name)}
                        checked={isDefaultCheck(item.name, item.defaultValue)}
                        onChange={(e) => {
                          return handleChange(
                            e.target.checked.toString(),
                            item.name
                          );
                        }}
                      />
                    );
                  }}
                />
              </When>
            </div>
          );
        })}
        <When
          condition={
            patchPolicy &&
            (patchPolicy === PatchPolicy.systemAll ||
              patchPolicy === PatchPolicy.securityOnly) &&
            !isEntityBaselineDependent
          }
        >
          <KeyValueList
            title="Settings defined by Patch Policy"
            items={
              !patchPolicy
                ? []
                : Object.entries(
                    patchPolicyRules[patchPolicy].defaultSettings
                  ).map(([key, value]) => ({
                    key,
                    value,
                  }))
            }
          />
        </When>
        <BaselineDefinedSettings
          isEntityBaselineDependent={isEntityBaselineDependent}
          parameters={
            visiblePatchPolicyParams.length > 0
              ? visiblePatchPolicyParams
              : stepState.selectedEntity?.inputParameters ?? []
          }
          renderValue={(value) => {
            const parsedValue = isValidJSONObject(value)
              ? JSON.parse(value)
              : value;

            const stringValue = Array.isArray(parsedValue)
              ? parsedValue.map((v) => v.toString()).join(", ")
              : parsedValue?.toString();

            return stringValue || "—";
          }}
        />
      </form>
    </FormProvider>
  );
}
